import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { onPrimaryColor } from '@/styles/darkHero.ts';

import { dayjs } from '@/utils/dayjsSetup.js';

import { StepLabel } from './step-label.tsx';

interface Props {
    slots: string[];
    selectedSlot?: string | null;
    invalidSlot?: string | null;
    onSelect: (slot: string) => void;
    testIdPrefix?: string;
}

const AFTERNOON_FROM_HOUR = 12;

const useStyles = makeStyles((theme: any) => ({
    groups: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1.25rem',
        width: '100%',
    },
    group: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
    },
    slots: {
        display: 'grid',
        gap: '0.5rem',
        gridTemplateColumns: 'repeat(auto-fill, minmax(6rem, 1fr))',
    },
    slot: {
        appearance: 'none',
        padding: '0.625rem 0.5rem',
        fontFamily: 'inherit',
        fontSize: '1rem',
        background: theme.palette.common.white,
        border: theme.border.light,
        borderRadius: theme.radius.field,
        color: theme.palette.common.darkGrey,
        cursor: 'pointer',
        transition: 'border-color 0.2s ease',
        '&:hover': {
            borderColor: theme.palette.common.yellow,
        },
        '&:focus-visible': {
            outline: `2px solid ${theme.palette.common.darkGrey}`,
            outlineOffset: '2px',
        },
    },
    selected: {
        background: theme.palette.common.yellow,
        color: onPrimaryColor(theme),
        borderColor: theme.palette.common.yellow,
        fontWeight: theme.weight.heading,
    },
    invalid: {
        borderColor: theme.palette.common.red,
        color: theme.palette.common.red,
        cursor: 'default',
        opacity: 0.6,
    },
}));

export const TimeslotSlotList = ({ slots, selectedSlot, invalidSlot, onSelect, testIdPrefix = 'booking' }: Props) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const morning = slots.filter((slot) => dayjs(slot).hour() < AFTERNOON_FROM_HOUR);
    const afternoon = slots.filter((slot) => dayjs(slot).hour() >= AFTERNOON_FROM_HOUR);

    const groups = [
        { key: 'morning', label: t('TimeslotMorning'), slots: morning },
        { key: 'afternoon', label: t('TimeslotAfternoon'), slots: afternoon },
    ].filter((group) => group.slots.length > 0);

    return (
        <div className={classes.groups}>
            {groups.map((group) => (
                <div key={group.key} className={classes.group}>
                    <StepLabel label={group.label} />
                    <div className={classes.slots}>
                        {group.slots.map((slot) => (
                            <button
                                key={slot}
                                type="button"
                                disabled={slot === invalidSlot}
                                aria-pressed={slot === selectedSlot}
                                className={clsx(
                                    classes.slot,
                                    slot === selectedSlot && classes.selected,
                                    slot === invalidSlot && classes.invalid
                                )}
                                onClick={() => onSelect(slot)}
                                data-testid={`${testIdPrefix}-timeslot-${slot}`}
                            >
                                {dayjs(slot).format('HH:mm')}
                            </button>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};
